import { useContext, useMemo, useState, useEffect } from "react";
import useModulesByCode from "@common/hooks/useModulesByCode";
import { ContextEDH } from "@context/ContextEDH";
import { getCatalogCategory } from "@utils/catalogsCategories";
import { useTranslation } from "react-i18next";

interface useGetCatalogsProps {
  searchQuery?: string;
  filterByCategory?: string;
}

interface useGetCatalogsReturn {
  catalogsOptions: any[];
  hasAccessToCatalogs: boolean;
  isLoading: boolean;
}

const useGetCatalogs = (props?: useGetCatalogsProps): useGetCatalogsReturn => {
  const {
    i18n: { language },
  } = useTranslation();
  const { searchQuery, filterByCategory } = props || {};
  const { state } = useContext(ContextEDH);
  const { modulesByCode } = useModulesByCode();
  const [isLoading, setIsLoading] = useState<boolean>(true);

  const hasAccessToCatalogs = useMemo(() => {
    return modulesByCode?.["catalogs"]?.accesible;
  }, [modulesByCode]);

  const catalogs = hasAccessToCatalogs ? (state?.base?.base?.catalogs ?? []) : [];

  useEffect(() => {
    if (catalogs?.length > 0) {
      setIsLoading(false);
    }
  }, [catalogs]);

  const catalogsOptions = useMemo(() => {
    return catalogs
      ?.filter(catalog => {
        const catalogWithCategory = getCatalogCategory(catalog);

        if (!catalog?.accesible || !catalog?.path) {
          return false;
        }

        if (searchQuery && !catalog?.label?.toLowerCase().includes(searchQuery.toLowerCase())) {
          return false;
        }

        if (
          filterByCategory &&
          catalogWithCategory?.category &&
          catalogWithCategory?.category !== filterByCategory &&
          filterByCategory !== "all"
        ) {
          return false;
        }

        return true;
      })
      ?.map(catalog => {
        const catalogWithCategory = getCatalogCategory(catalog);

        return {
          label: `${catalogWithCategory?.label} ${catalogWithCategory?.category ?? ""}`,
          value: catalogWithCategory?.id,
          self: catalogWithCategory,
        };
      });
  }, [catalogs, searchQuery, filterByCategory, language]);

  return { catalogsOptions, hasAccessToCatalogs, isLoading };
};

export default useGetCatalogs;
